import React, { useState } from "react";
import { Box, Grid, Typography, Avatar, Divider } from "@mui/material";
import {
  BCPTrophy,
  BCPFlag,
  BCPDiceD20
} from "../../../components/icons/bcp/BCPIcons";

const LEADER_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706",
  isDark ? "#f87171" : "#dc2626",
  isDark ? "#22d3ee" : "#0891b2"
];

export default function EnemyFactions({ data, theme }) {
  const sorted = [...data].sort((a, b) => b.overall.winRate - a.overall.winRate);
  const [selectedEnemy, setSelectedEnemy] = useState(sorted[0]);

  if (!data || data.length === 0) return null;

  const isDark = theme.palette.mode === "dark";
  const leaderColors = LEADER_COLORS(isDark);

  const containerSx = {
    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 1,
    p: 2.5,
    boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)"
  };

  const getWinRateColor = (winRate) => {
    if (winRate >= 55) return isDark ? "#4ade80" : "#16a34a";
    if (winRate <= 45) return isDark ? "#f87171" : "#dc2626";
    return isDark ? "#9ca3af" : "#6b7280";
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const mostPlayed = [...data].sort((a, b) => b.overall.games - a.overall.games)[0];

  const highlights = [
    {
      title: "Best Matchup",
      icon: BCPTrophy,
      color: isDark ? "#4ade80" : "#16a34a",
      faction: sorted[0],
      stat: `${sorted[0].overall.winRate}% win rate`
    },
    {
      title: "Toughest Opponent",
      icon: BCPFlag,
      color: isDark ? "#f87171" : "#dc2626",
      faction: sorted[sorted.length - 1],
      stat: `${sorted[sorted.length - 1].overall.winRate}% win rate`
    },
    {
      title: "Most Played",
      icon: BCPDiceD20,
      color: isDark ? "#3b82f6" : "#2563eb",
      faction: mostPlayed,
      stat: `${mostPlayed.overall.games} games`
    }
  ];

  const renderHighlightCard = ({ title, icon: Icon, color, faction, stat }) => (
    <Box
      sx={{
        ...containerSx,
        borderTop: `3px solid ${color}`,
        textAlign: "center",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
        }
      }}
      onClick={() => setSelectedEnemy(faction)}
    >
      <Box sx={{ mb: 1 }}>
        <Icon size="sm" style={{ color }} />
      </Box>
      <Typography
        sx={{
          color,
          fontSize: "0.6rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 1
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 0.75 }}>
        <Avatar
          src={faction.faction.icon || undefined}
          sx={{ width: 20, height: 20, fontSize: "0.55rem", fontWeight: 700 }}
        >
          {!faction.faction.icon && getInitials(faction.faction.name)}
        </Avatar>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.8rem",
            color: theme.palette.text.primary,
            lineHeight: 1.2
          }}
        >
          {faction.faction.name}
        </Typography>
      </Box>
      <Typography sx={{ color, fontWeight: 700, fontSize: "1.1rem", lineHeight: 1 }}>
        {stat}
      </Typography>
    </Box>
  );

  const renderDetailStatRow = (label, labelColor, stats, isLast) => {
    const winRateColor = getWinRateColor(stats.winRate);
    return (
      <Box key={label}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
          <Box
            sx={{
              width: 68,
              flexShrink: 0,
              px: 0.75,
              py: 0.35,
              borderRadius: 0.75,
              border: `1px solid ${labelColor}44`,
              backgroundColor: `${labelColor}18`,
              textAlign: "center"
            }}
          >
            <Typography
              sx={{
                color: labelColor,
                fontSize: "0.6rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                lineHeight: 1.3
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 0.75
              }}
            >
              <Typography sx={{ color: winRateColor, fontWeight: 700, fontSize: "0.875rem" }}>
                {stats.winRate}% win rate
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {stats.games.toLocaleString()} games
              </Typography>
            </Box>
            <Box
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${stats.winRate}%`,
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${winRateColor}55, ${winRateColor})`
                }}
              />
            </Box>
          </Box>
        </Box>
        {!isLast && (
          <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Divider sx={{ mb: 4, borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }} />

      <Typography
        variant="h5"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textAlign: "center",
          letterSpacing: "0.05em"
        }}
      >
        Enemy Factions
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {highlights.map((h) => (
          <Grid item xs={12} sm={4} key={h.title}>
            {renderHighlightCard(h)}
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        <Grid item xs={12} md={5} sx={{ display: "flex" }}>
          <Box sx={{ ...containerSx, flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
            >
              All Matchups
            </Typography>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {sorted.map((enemy, i) => {
                const isSelected = selectedEnemy?.faction.id === enemy.faction.id;
                const color = getWinRateColor(enemy.overall.winRate);
                return (
                  <Box key={enemy.faction.id}>
                    <Box
                      onClick={() => setSelectedEnemy(enemy)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.25,
                        px: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        borderLeft: `3px solid ${isSelected ? "#3b82f6" : "transparent"}`,
                        backgroundColor: isSelected
                          ? (isDark ? "rgba(59,130,246,0.1)" : "rgba(37,99,235,0.06)")
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? (isDark ? "rgba(59,130,246,0.14)" : "rgba(37,99,235,0.09)")
                            : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)")
                        }
                      }}
                    >
                      <Avatar
                        src={enemy.faction.icon || undefined}
                        sx={{ width: 28, height: 28, fontSize: "0.6rem", fontWeight: 700, flexShrink: 0 }}
                      >
                        {!enemy.faction.icon && getInitials(enemy.faction.name)}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: "0.8rem",
                            color: isSelected
                              ? theme.palette.text.primary
                              : theme.palette.text.secondary,
                            mb: 0.4,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {enemy.faction.name}
                        </Typography>
                        <Box
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                            overflow: "hidden"
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${enemy.overall.winRate}%`,
                              borderRadius: 2,
                              background: `linear-gradient(90deg, ${color}55, ${color})`
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                        <Typography
                          sx={{
                            color,
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            lineHeight: 1.2,
                            fontVariantNumeric: "tabular-nums"
                          }}
                        >
                          {enemy.overall.winRate}%
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.65rem",
                            fontVariantNumeric: "tabular-nums"
                          }}
                        >
                          {enemy.overall.games} games
                        </Typography>
                      </Box>
                    </Box>

                    {i !== sorted.length - 1 && (
                      <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7} sx={{ display: "flex" }}>
          {selectedEnemy && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
              <Box sx={containerSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                    vs
                  </Typography>
                  <Avatar
                    src={selectedEnemy.faction.icon || undefined}
                    sx={{ width: 28, height: 28, fontSize: "0.6rem", fontWeight: 700 }}
                  >
                    {!selectedEnemy.faction.icon && getInitials(selectedEnemy.faction.name)}
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
                  >
                    {selectedEnemy.faction.name}
                  </Typography>
                </Box>

                {renderDetailStatRow(
                  "Overall",
                  isDark ? "#9ca3af" : "#6b7280",
                  selectedEnemy.overall,
                  false
                )}
                {renderDetailStatRow("Attacker", "#8b5cf6", selectedEnemy.asAttacker, false)}
                {renderDetailStatRow("Defender", "#3b82f6", selectedEnemy.asDefender, true)}
              </Box>

              {selectedEnemy.vsLeaders && selectedEnemy.vsLeaders.length > 0 && (
                <Box sx={{ ...containerSx, flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}
                  >
                    vs {selectedEnemy.faction.name} Leaders
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {selectedEnemy.vsLeaders.map((leader, i) => {
                      const leaderColor = leaderColors[i % leaderColors.length];
                      const winRateColor = getWinRateColor(leader.winRate);
                      const isLast = i === selectedEnemy.vsLeaders.length - 1;
                      return (
                        <Box key={i}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
                            <Avatar
                              src={leader.icon || undefined}
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                backgroundColor: !leader.icon ? leaderColor : undefined,
                                flexShrink: 0
                              }}
                            >
                              {!leader.icon && getInitials(leader.name)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  justifyContent: "space-between",
                                  mb: 0.5
                                }}
                              >
                                <Typography
                                  sx={{ color: winRateColor, fontWeight: 700, fontSize: "0.8rem" }}
                                >
                                  {leader.winRate}% win rate
                                </Typography>
                                <Typography
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.7rem",
                                    fontVariantNumeric: "tabular-nums"
                                  }}
                                >
                                  {leader.games.toLocaleString()} games
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  height: 5,
                                  borderRadius: 3,
                                  backgroundColor: isDark
                                    ? "rgba(255,255,255,0.08)"
                                    : "rgba(0,0,0,0.08)",
                                  overflow: "hidden",
                                  mb: 0.4
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${leader.winRate}%`,
                                    borderRadius: 3,
                                    background: `linear-gradient(90deg, ${winRateColor}55, ${winRateColor})`
                                  }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: "0.7rem",
                                  fontWeight: 500
                                }}
                              >
                                {leader.name}
                              </Typography>
                            </Box>
                          </Box>
                          {!isLast && (
                            <Divider
                              sx={{
                                borderColor: isDark
                                  ? "rgba(255,255,255,0.06)"
                                  : "rgba(0,0,0,0.06)"
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
